import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Typography,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Divider,
} from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import HealthAndSafetyIcon from "@mui/icons-material/HealthAndSafety";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import GlassCard from "../../../components/common/GlassCard";

function DemographicsInsuranceCard({ patient, insurance, onAddInsurance, onDeleteInsurance, onEditDemographics }) {
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [activeInsuranceId, setActiveInsuranceId] = useState(null);

  const handleMenuOpen = (event, id) => {
    setMenuAnchor(event.currentTarget);
    setActiveInsuranceId(id);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setActiveInsuranceId(null);
  };

  return (
    <GlassCard
      title="Demographics & Insurance"
      icon={<PersonIcon color="primary" />}
      onAdd={onAddInsurance}
      addLabel="Add Insurance"
    >
      {/* Demographics Section */}
      {patient && (
        <Box mb={2}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              DEMOGRAPHICS
            </Typography>
            {onEditDemographics && (
              <Tooltip title="Edit Demographics">
                <IconButton
                  size="small"
                  onClick={onEditDemographics}
                  data-testid="edit-demographics-button"
                  data-action="edit-demographics"
                  aria-label="Edit demographics"
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
          <Box mt={0.5}>
            <Typography variant="body2">
              <strong>DOB:</strong> {patient.dob ? new Date(patient.dob).toLocaleDateString() : "N/A"}
            </Typography>
            <Typography variant="body2">
              <strong>Sex:</strong> {patient.sex || "N/A"}
            </Typography>
            <Typography variant="body2">
              <strong>Phone:</strong> {patient.phone || "N/A"}
            </Typography>
            <Typography variant="body2">
              <strong>Email:</strong> {patient.email || "N/A"}
            </Typography>
            {patient.address && (
              <Typography variant="body2">
                <strong>Address:</strong> {patient.address}
              </Typography>
            )}
          </Box>
        </Box>
      )}

      <Divider sx={{ my: 1.5 }} />

      {/* Insurance Section */}
      <Box>
        <Box display="flex" alignItems="center" gap={0.5} mb={1}>
          <HealthAndSafetyIcon fontSize="small" color="info" />
          <Typography variant="caption" color="text.secondary" fontWeight={600}>
            INSURANCE
          </Typography>
        </Box>
        
        {insurance?.length ? (
          insurance.map((ins) => (
            <Box
              key={ins.id}
              mb={1}
              sx={{
                p: 1,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                bgcolor: "background.paper",
              }}
              data-testid={`insurance-item-${ins.id}`}
              data-insurance-company={ins.insurance_company}
            >
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box flex={1}>
                  <Typography variant="body2" fontWeight={500}>
                    {ins.insurance_company}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Policy: {ins.policy_number}
                  </Typography>
                  {ins.group_number && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      Group: {ins.group_number}
                    </Typography>
                  )}
                </Box>
                <Box display="flex" gap={0.5} alignItems="center">
                  {ins.is_primary && (
                    <Chip 
                      label="Primary" 
                      size="small" 
                      color="success" 
                      variant="outlined"
                    />
                  )}
                  {onDeleteInsurance && (
                    <Tooltip title="Options">
                      <IconButton 
                        size="small" 
                        onClick={(e) => handleMenuOpen(e, ins.id)}
                        data-testid={`insurance-menu-button-${ins.id}`}
                        aria-label="Open insurance options"
                        data-action="insurance-menu"
                        data-insurance-id={ins.id}
                      >
                        <MoreVertIcon 
                          aria-label="Open insurance options" 
                          fontSize="small"
                        />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </Box>
            </Box>
          ))
        ) : (
          <Typography variant="body2" color="text.secondary">
            No insurance on file
          </Typography>
        )}
      </Box>

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={handleMenuClose}>
        <MenuItem
          onClick={() => {
            handleMenuClose();
            if (activeInsuranceId && onDeleteInsurance) {
              onDeleteInsurance(activeInsuranceId);
            }
          }}
          data-testid={`insurance-delete-menu-item-${activeInsuranceId}`}
          aria-label="Delete insurance"
          data-action="delete-insurance"
          data-insurance-id={activeInsuranceId}
        >
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} /> Delete
        </MenuItem>
      </Menu>
    </GlassCard>
  );
}

DemographicsInsuranceCard.propTypes = {
  patient: PropTypes.object,
  insurance: PropTypes.array,
  onAddInsurance: PropTypes.func.isRequired,
  onDeleteInsurance: PropTypes.func,
  onEditDemographics: PropTypes.func,
};

DemographicsInsuranceCard.defaultProps = {
  insurance: [],
};

export default DemographicsInsuranceCard;
